import { checkIsStringEmpty } from "features/Common/modules/DataTypes/Strings.utils";
import {
  BANNER_DETAILS,
  DEFAULT_GST_HSN,
  E_INVOICE_STATUS,
  WORKFLOWS,
} from "./EditInvoiceModal.constants";

/**
 * Extracts and maps default values from a transaction record into form schema keys.
 * Handles fallback cases for nested properties such as customer phone, billing state, and GST info.
 *
 * @param {Object} record - The transaction database row record.
 * @returns {Object} Structured form keys matching form schema.
 */
export const getOriginalValues = (record) => {
  if (!record) return {};
  const gstInfo = record.customer_gst_info || {};

  const rawPhone = gstInfo.customer_phone || record.customer_phone || "";
  const countryCode =
    (record.customer_country_code || "").replace(/\D/g, "") || "91";
  const cleanPhone = (rawPhone || "").replace(/\D/g, "");

  let phone = "";
  if (cleanPhone) {
    phone = cleanPhone.startsWith(countryCode)
      ? cleanPhone
      : `${countryCode}${cleanPhone}`;
  }

  return {
    companyName: gstInfo?.company_name || record?.company_name || "",
    phone,
    email: gstInfo?.customer_email || record?.customer_email || "",
    companyAddress:
      gstInfo?.company_address ||
      record?.booking_address ||
      record?.company_address ||
      "",
    pincode:
      gstInfo?.company_pincode ||
      record?.pincode ||
      record?.company_pincode ||
      "",
    state: gstInfo?.billing_state || record?.billing_state || "",
    amount: String(record?.transaction_amount || record?.payable_amount || ""),
    gstin: gstInfo?.gst_number || record?.gst_number || record?.gstin || "",
    hsnCode: gstInfo?.hsn_code || record?.hsn_code || DEFAULT_GST_HSN.hsn,
    gstPercentage:
      gstInfo?.gst_percentage != null && gstInfo?.gst_percentage !== ""
        ? String(gstInfo?.gst_percentage)
        : record?.gst_percentage != null && record?.gst_percentage !== ""
        ? String(record?.gst_percentage)
        : record?.creator_gst_percent != null &&
          record?.creator_gst_percent !== ""
        ? String(record?.creator_gst_percent)
        : DEFAULT_GST_HSN.gst,
  };
};

/**
 * Determines the edit invoice workflow path based on e-invoice status.
 * If e-invoicing is enabled and status is success (2), runs the e-invoice workflow directly.
 * Otherwise, falls back to the normal flow where user confirmation for GST filing is prompted.
 *
 * @param {Object} record - The transaction record.
 * @returns {string} The matched workflow key.
 */
export const determineWorkflow = (record) => {
  const einvoiceEnabled = record?.einvoice_enabled || record?.einvoice_enable;
  const einvoiceStatus = record?.einvoice_status;

  if (einvoiceEnabled === true && einvoiceStatus === E_INVOICE_STATUS.SUCCESS) {
    return WORKFLOWS.EINVOICE_FLOW;
  }
  return WORKFLOWS.NORMAL_FLOW;
};

/**
 * Returns banner information (text, style class names) depending on active workflow.
 *
 * @param {string} workflow - The workflow type.
 * @returns {Object|null} Banner config object or null.
 */
export const getBannerInfo = (workflow) => {
  if (workflow === WORKFLOWS.EINVOICE_FLOW) {
    return {
      text: BANNER_DETAILS.text,
      className: BANNER_DETAILS.className,
    };
  }
  return null;
};

/**
 * Validates invoice form inputs against compliance rules (mandatory values, numeric structures, emails, GSTIN patterns).
 *
 * @param {Object} formData - Current form values.
 * @returns {Object} Key-value pairs of error messages.
 */
export const validateFields = (formData, isCreditNoteFlow) => {
  const errors = {};

  // Company Name validation (mandatory only in credit note flow)
  if (isCreditNoteFlow && checkIsStringEmpty(formData.companyName)) {
    errors.companyName = "Company name is required";
  }

  // Optional Phone validation: 10 to 15 digits (including country code)
  if (!checkIsStringEmpty(formData.phone)) {
    const phoneDigits = formData.phone.trim().replace(/\D/g, "");
    if (phoneDigits.length < 10 || phoneDigits.length > 15) {
      errors.phone = "Please enter a valid phone number";
    }
  }

  // Optional Email format validation
  if (!checkIsStringEmpty(formData.email)) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      errors.email = "Please enter a valid email address";
    }
  }

  // Company Address validation (mandatory only in credit note flow)
  if (isCreditNoteFlow && checkIsStringEmpty(formData.companyAddress)) {
    errors.companyAddress = "Company address is required";
  }

  // Pincode validation: mandatory 6 digits in credit note flow, optional but must be 6 digits if entered in normal flow
  if (checkIsStringEmpty(formData.pincode)) {
    if (isCreditNoteFlow) {
      errors.pincode = "Pincode is required";
    }
  } else {
    const pincodeRegex = /^[0-9]{6}$/;
    if (!pincodeRegex.test(formData.pincode.trim())) {
      errors.pincode = "Pincode must be exactly 6 digits";
    }
  }

  // State selection validation
  if (checkIsStringEmpty(formData.state)) {
    errors.state = "State is required";
  }

  // Optional GSTIN pattern validation (standard Indian GST format)
  if (!checkIsStringEmpty(formData.gstin)) {
    const gstinRegex =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i;
    if (!gstinRegex.test(formData.gstin.trim())) {
      errors.gstin = "Please enter a valid 15-digit GSTIN";
    }
  }

  if (!checkIsStringEmpty(formData.gstPercentage)) {
    const gstNum = Number(formData.gstPercentage);
    if (isNaN(gstNum) || gstNum < 0 || gstNum > 100) {
      errors.gstPercentage = "GST percentage must be between 0 and 100";
    }
  }

  return errors;
};

/**
 * Calculates base value, original GST, new GST amounts and the difference.
 * Shared source of truth calculation function.
 */
export const calculateGstDifference = (
  totalAmount,
  originalGst,
  updatedGst
) => {
  const amt = Number(totalAmount) || 0;
  const orig = Number(originalGst) || 0;
  const updated = Number(updatedGst) || 0;

  const baseAmount = amt / (1 + orig / 100);
  const originalGstAmt = baseAmount * (orig / 100);
  const newGstAmt = baseAmount * (updated / 100);
  const gstDiff = originalGstAmt - newGstAmt;
  const isGstLower = updated < orig;

  return {
    baseAmount,
    originalGstAmt,
    newGstAmt,
    gstDiff,
    isGstLower,
  };
};
